import type { FormEvent } from "react";
import { useEffect, useState } from "react";

import { Button } from "@/atoms";
import { ProfileDetails, ProfileForm, ProfileHeader } from "@/components/profile";
import { strings } from "@/constants";
import { AlertVariantEnum, ButtonVariantEnum, RoleEnum } from "@/enums";
import { useAuth, useToast, useUpdateCompanyProfile } from "@/hooks";

export const ProfilePage = () => {
    const [isEditing, setIsEditing] = useState(false);

    const {
        onFetchUser,
        user,
    } = useAuth();

    const { onShow: showToastHandler } = useToast();

    const [name, setName] = useState(user?.name ?? "");

    const [companyName, setCompanyName] = useState(user?.companyName ?? "");

    const [phone, setPhone] = useState(user?.phone ?? "");

    const [email, setEmail] = useState(user?.email ?? "");

    const [password, setPassword] = useState("");

    const [confirmPassword, setConfirmPassword] = useState("");

    const [passwordError, setPasswordError] = useState("");

    const {
        isLoading: isSaving,
        onRun: runUpdateProfileHandler,
    } = useUpdateCompanyProfile();

    const isAdmin = user?.role === RoleEnum.ADMIN;

    const roleLabel = isAdmin ? strings.profile.roleAdmin : strings.profile.roleCompany;

    const startEditHandler = () => setIsEditing(true);

    const cancelEditHandler = () => {
        setName(user?.name ?? "");

        setCompanyName(user?.companyName ?? "");

        setPhone(user?.phone ?? "");

        setEmail(user?.email ?? "");

        setPassword("");

        setConfirmPassword("");

        setPasswordError("");

        setIsEditing(false);
    };

    const submitHandler = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (password && password !== confirmPassword) {
            setPasswordError(strings.profile.passwordMismatch);

            return;
        }

        setPasswordError("");

        try {
            await runUpdateProfileHandler({
                companyName,
                name,
                phone,
            });

            await onFetchUser();

            showToastHandler({
                message: strings.profile.updateSuccess,
                variant: AlertVariantEnum.SUCCESS,
            });

            setPassword("");

            setConfirmPassword("");

            setIsEditing(false);
        } catch (err) {
            showToastHandler({
                message: err instanceof Error ? err.message : strings.profile.updateError,
                variant: AlertVariantEnum.ERROR,
            });
        }
    };

    useEffect(
        () => {
            setName(user?.name ?? "");

            setCompanyName(user?.companyName ?? "");

            setPhone(user?.phone ?? "");

            setEmail(user?.email ?? "");
        },
        [
            user?.name,
            user?.companyName,
            user?.phone,
            user?.email,
        ],
    );

    return (
        <div
            className="
                mx-auto
                w-full
                max-w-5xl
                space-y-4
            "
        >
                <div
                    style={{
                        backdropFilter: "blur(24px) saturate(180%)",
                        boxShadow: "0 8px 32px rgba(19,86,188,0.10), inset 0 1px 0 rgba(255,255,255,0.7)",
                        WebkitBackdropFilter: "blur(24px) saturate(180%)",
                    }}
                    className="
                        overflow-hidden
                        rounded-default
                        border
                        border-white/55
                        bg-white/55
                        md:grid
                        md:grid-cols-[18rem_1fr]
                    "
                >
                    <ProfileHeader
                        isAdmin={isAdmin}
                        roleLabel={roleLabel}
                        user={user}
                    />
                    <div className="flex flex-col">
                        {isEditing ? (
                            <ProfileForm
                                companyName={companyName}
                                confirmPassword={confirmPassword}
                                email={email}
                                isAdmin={isAdmin}
                                isSaving={isSaving}
                                name={name}
                                password={password}
                                passwordError={passwordError}
                                phone={phone}
                                onCancel={cancelEditHandler}
                                onChangeCompanyName={setCompanyName}
                                onChangeConfirmPassword={setConfirmPassword}
                                onChangeEmail={setEmail}
                                onChangeName={setName}
                                onChangePassword={setPassword}
                                onChangePhone={setPhone}
                                onSubmit={submitHandler}
                            />
                        ) : (
                            <>
                                <ProfileDetails
                                    isAdmin={isAdmin}
                                    roleLabel={roleLabel}
                                    user={user}
                                />
                                <div
                                    className="
                                        mt-auto
                                        flex
                                        justify-end
                                        border-t
                                        border-gray-100
                                        px-4
                                        py-3
                                        sm:px-6
                                        sm:py-4
                                    "
                                >
                                    <Button
                                        variant={ButtonVariantEnum.PRIMARY}
                                        onClick={startEditHandler}
                                    >
                                        {strings.profile.editButton}
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
    );
};
